import React from 'react'

const BACKTICK_TOKEN = /`([^`\n]+)`/g

/**
 * Splits a translation string on `` `Label` `` markers and renders each marker
 * as a styled chip — visually parallel to a {@code <kbd>} chord, distinct from
 * surrounding prose. Everything outside the markers is rendered as plain text.
 *
 * The token form is the EN sentence-case escape mechanism for one-off UI-label
 * references inline in prose (see {@link ../../../.claude/rules/i18n-casing.md}).
 */
export function renderInlineMarkup(text: string): React.ReactNode {
    if (!text.includes('`')) return text
    const parts: React.ReactNode[] = []
    let lastIndex = 0
    let match: RegExpExecArray | null
    BACKTICK_TOKEN.lastIndex = 0
    let chipIndex = 0
    while ((match = BACKTICK_TOKEN.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(
                <React.Fragment key={`t-${lastIndex}`}>
                    {text.slice(lastIndex, match.index)}
                </React.Fragment>,
            )
        }
        parts.push(<UiLabelChip key={`c-${chipIndex++}`}>{match[1]}</UiLabelChip>)
        lastIndex = match.index + match[0].length
    }
    if (lastIndex < text.length) {
        parts.push(
            <React.Fragment key={`t-${lastIndex}`}>
                {text.slice(lastIndex)}
            </React.Fragment>,
        )
    }
    return parts
}

interface InlineMarkupProps {
    text: string
}

export function InlineMarkup({text}: InlineMarkupProps) {
    return <>{renderInlineMarkup(text)}</>
}

interface UiLabelChipProps {
    children: React.ReactNode
}

function UiLabelChip({children}: UiLabelChipProps) {
    return (
        <code className="inline-flex items-center rounded border border-border bg-muted px-1 py-0 text-[0.85em] font-medium font-sans text-foreground align-baseline">
            {children}
        </code>
    )
}
